import type { SchemaField } from '@cli-types/core-engine/schema.js'
import type { PathPart } from '@cli-types/next-adapter/forms.js'
import { safeIdentifier, singularize } from '@cli-utils/text.js'
import { renderNestedObjectListFieldRenderer } from '@next-adapter/templates/renderers/generators/form/field-jsx-nested/render-nested-object-list-field.js'
import { findTitlePath } from './find-title-path.js'
import { objectValueForFields } from './object-value-for-fields.js'
import { pathExpression } from './path-expression.js'
import { renderNestedField } from './render-nested-field.js'
import { requiredLeafPaths } from './required-leaf-paths.js'

export function renderNestedObjectListField(
  field: SchemaField,
  indent: string,
  label: string,
  path: PathPart[],
  depth: number
): string {
  const singularLabel = singularize(label)
  const itemIndexVar = `${safeIdentifier(field.name, 'nestedList')}Index${depth}`
  const titlePath = findTitlePath(field.fields ?? [])
  const titlePathSuffix = titlePath ? `.${titlePath.join('.')}` : ''
  const renderTitleProp = titlePath
    ? renderNestedObjectListFieldRenderer({
        kind: 'renderNestedObjectListFieldContentTemplate',
        props: {
          indent: indent,
          itemIndexVar: itemIndexVar,
          pathString: path
            .map((part) => (typeof part === 'string' ? part : `\${${part.expression}}`))
            .join('.'),
          titlePathSuffix: titlePathSuffix,
          singularLabel: singularLabel
        }
      })
    : ''
  const nestedFieldsJSX = (field.fields ?? [])
    .map((child) =>
      renderNestedField(
        child,
        `${indent}      `,
        [...path, { expression: itemIndexVar }, child.name],
        depth + 1
      )
    )
    .filter(Boolean)
    .join('\n')
  const defaultItem = objectValueForFields(field.fields ?? [])
  const descriptionProp = field.hint
    ? `\n${indent}  description={${JSON.stringify(field.hint)}}`
    : ''
  const hideLabelProp = field.label ? '' : `\n${indent}  hideLabel`
  const maxItemsProp = field.maxItems ? `\n${indent}  maxItems={${field.maxItems}}` : ''
  const validatePaths = requiredLeafPaths(field.fields ?? [])
  const validatePathsProp =
    validatePaths.length > 0 ? `\n${indent}  validatePaths={${JSON.stringify(validatePaths)}}` : ''

  return renderNestedObjectListFieldRenderer({
    kind: 'renderNestedObjectListFieldNestedObjectListFieldTemplate',
    props: {
      indent: indent,
      pathExpression: pathExpression(path),
      stringify: JSON.stringify(label),
      stringifySerializedValue: JSON.stringify(singularLabel),
      defaultItem: defaultItem,
      hideLabelProp: hideLabelProp,
      descriptionProp: descriptionProp,
      maxItemsProp: maxItemsProp,
      validatePathsProp: validatePathsProp,
      renderTitleProp: renderTitleProp,
      itemIndexVar: itemIndexVar,
      nestedFieldsJsx: nestedFieldsJSX
    }
  })
}
